import { type FC, useCallback, useState } from 'react';
import { Card } from '../../../Card';
import {
  type TeamMembersQueryParams,
  type TeamMembersQueryResponse,
  useTeamMembersQuery,
} from '../../../../apiHooks/useTeamMembersQuery';
import styled from 'styled-components';
import { Pagination } from '../../../Pagination';
import { Table } from '../../../Table';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import ExportActivityLogButton from '../../../Button/DownloadButton/ExportActivityLogButton/ExportActivityLogButton';
import { useTeamMembersFilters } from '../../hooks/useTeamMembersFilters';
import { useTeamMembersColumns } from '../../hooks/useTeamMembersColumns';
import { ActiveFilterPill } from '../ActiveFilterPill';

const ITEMS_PER_PAGE: number = 10 as const;

export const TeamMembers: FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [teamMembersParams, setTeamMembersParams] = useState<TeamMembersQueryParams>({
    page: { skip: 0, take: ITEMS_PER_PAGE },
    sort: { sortDirection: 1, property: 'cyberiq' },
    filters: [],
  });

  const { data: teamMembersQueryResponse } = useTeamMembersQuery(teamMembersParams);
  const teamMembersData: TeamMembersQueryResponse = teamMembersQueryResponse ?? {
    total: 0,
    skip: null,
    take: null,
    values: [],
  };

  const onNewParams = useCallback((params: Partial<TeamMembersQueryParams>) => {
    if (params.filters) {
      // Reset page filters
      params.page = { skip: 0, take: ITEMS_PER_PAGE };
      setCurrentPage(1);
    }

    setTeamMembersParams((prev) => {
      let mergedFilters = [...(prev.filters || [])];

      if (params.filters) {
        if (params.filters.length === 0) {
          mergedFilters = [];
        } else {
          params.filters.forEach((newFilter) => {
            mergedFilters = mergedFilters.filter((filter) => filter.property !== newFilter.property);
            if (newFilter.value !== '') {
              mergedFilters.push(newFilter);
            }
          });
        }
      }

      return {
        ...prev,
        ...params,
        filters: mergedFilters,
      };
    });
  }, []);

  const { nameSearchInputValue, handleNameSearchChange, clearFilters } = useTeamMembersFilters(onNewParams);

  const handlePageChange = (page: number) => {
    onNewParams({ page: { take: ITEMS_PER_PAGE, skip: (page - 1) * ITEMS_PER_PAGE } });
    setCurrentPage(page);
  };

  const columns = useTeamMembersColumns(nameSearchInputValue, handleNameSearchChange);

  return (
    <Card>
      <TeamMembersSection>
        <SectionHeader>
          <Title>
            <NamedContentEntryText refKey="managerSectionTitles" container="span" subKey="teamMembers" />
          </Title>
          {!!teamMembersParams.filters?.length && (
            <ActiveFilterPill onClear={clearFilters} filterCount={teamMembersData.total} />
          )}
          <ExportActivityLogButton />
        </SectionHeader>
        <StyledTable data={teamMembersData.values} columns={columns} />
        <Pagination
          pages={Math.ceil(teamMembersData.total / ITEMS_PER_PAGE)}
          currentPage={currentPage}
          perPage={ITEMS_PER_PAGE}
          listSize={teamMembersData.total}
          onChange={handlePageChange}
        />
      </TeamMembersSection>
    </Card>
  );
};

const TeamMembersSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  text-transform: uppercase;
`;

const StyledTable = styled(Table)`
  @media (max-width: 768px) {
    table {
      width: 900px;
    }
  }
` as typeof Table;
